import { useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
//
import {
  Box,
  Button,
  Card,
  Container,
  Alert,
  AlertTitle,
  FormControlLabel,
  Popover,
  Switch,
  TextField,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Stack,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { InvoicesRunTableRow, InvoiceRunsTableToolbar } from '../components';
// api
import { getAllInvoiceRuns, startInvoiceRun } from '@/features/invoices/api';
import { InvoiceRun } from '@/features/invoices/types';
// hookes + utils
import { useGetAccess } from '@/hooks/useAccess';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
//
import {
  INVOICE_RUNS_TABLE_HEAD,
  applySortFilterInvoices,
} from '@/features/invoices/utils';
import { PATH_USER } from '@/features/app';
import { consoleError } from '@/utils/consoleError';

export default function InvoiceRunList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
    setSelected,
    setPage,
  } = useTable();

  const allow = useGetAccess('provider_invoices');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const { themeStretch } = useSettings();
  const queryClient = useQueryClient();
  const [filterStartDate, setFilterStartDate] = useState<string | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<string | null>(null);
  const [runAnchorEl, setRunAnchorEl] = useState<HTMLElement | null>(null);
  const [runCutoffDate, setRunCutoffDate] = useState<string>('');
  const [runDaysCount, setRunDaysCount] = useState<string>('');
  const [runIsDryRun, setRunIsDryRun] = useState<boolean>(false);

  // =================
  //      QUERIES
  // =================
  const { data: invoiceRuns, isLoading: invoicesRunLoading } = useQuery({
    queryKey: ['invoiceRuns'],
    queryFn: getAllInvoiceRuns,
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching invoices`);
    },
  });

  const startInvoiceRunMutation = useMutation({
    mutationFn: ({
      cutoffDate,
      daysCount,
      isDryRun,
    }: {
      cutoffDate: string;
      daysCount: number;
      isDryRun: boolean;
    }) => startInvoiceRun({ cutoffDate, daysCount, isDryRun }),
    onSuccess: () => {
      successMsg('Invoice run started successfully.');
      queryClient.invalidateQueries(['invoiceRuns']);
      setRunCutoffDate('');
      setRunDaysCount('');
      setRunIsDryRun(false);
      handleCloseRunPopover();
    },
    onError: (error) => {
      errorMsg(error, `Something went wrong while starting invoice run`);
    },
  });

  const resetFilters = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
  };

  const handleDatepickerChange = (date: string | null, date_type: string) => {
    switch (date_type) {
      case 'start':
        setFilterStartDate(date);
        break;
      case 'end':
        setFilterEndDate(date);
        break;
      default:
        consoleError('InvoiceRun', 'handleDatepickerChange()');
    }
    setSelected([]);
    setPage(0);
  };

  const dataFiltered = applySortFilterInvoices({
    tableData: invoiceRuns || [],
    filterStartDate,
    filterEndDate,
    comparator: getComparator(order, orderBy),
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length;

  const handleOpenRunPopover = (event: MouseEvent<HTMLElement>) => {
    setRunAnchorEl(event.currentTarget);
  };

  const handleCloseRunPopover = () => {
    setRunAnchorEl(null);
  };

  const handleSubmitInvoiceRun = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!runCutoffDate || !runDaysCount) {
      errorMsg(
        new Error('Cutoff date and days count are required'),
        'Unable to start invoice run'
      );
      return;
    }

    const parsedDays = Number.parseInt(runDaysCount, 10);
    if (Number.isNaN(parsedDays) || parsedDays <= 0) {
      errorMsg(
        new Error('Days count must be a positive number'),
        'Unable to start invoice run'
      );
      return;
    }

    startInvoiceRunMutation.mutate({
      cutoffDate: runCutoffDate,
      daysCount: parsedDays,
      isDryRun: runIsDryRun,
    });
  };

  const isRunPopoverOpen = Boolean(runAnchorEl);
  const runPopoverId = isRunPopoverOpen ? 'invoice-run-popover' : undefined;

  const asNumber = (value: unknown) => {
    if (Array.isArray(value)) return value.length;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
      if (value.toLowerCase?.() === 'true') return 1;
    }
    if (value === true) return 1;
    return 0;
  };

  const asBoolean = (value: unknown) => asNumber(value) > 0;

  const getRegenerationSignals = (run: InvoiceRun) => {
    const directFlag = asBoolean(run.regeneration_required);

    const entries = Object.entries(run || {}).filter(([key]) =>
      key.toLowerCase().includes('regener')
    );

    let flag = directFlag;
    let count = 0;

    entries.forEach(([, value]) => {
      flag = flag || asBoolean(value);
      count = Math.max(count, asNumber(value));
    });

    const providerInvoices = (run as any).provider_invoices;
    if (Array.isArray(providerInvoices)) {
      flag =
        flag ||
        providerInvoices.some((invoice) => asBoolean(invoice?.regenerate));
      count = Math.max(
        count,
        ...providerInvoices.map((invoice) => asNumber(invoice?.regenerate))
      );
    }

    // fallback to explicit fields if no matching keys were found
    if (!entries.length && !directFlag) {
      flag =
        asBoolean(run.regenerate) ||
        asBoolean(run.regenerate_invoices) ||
        asBoolean(run.regeneration_required) ||
        flag;
      count = Math.max(
        count,
        asNumber(run.regenerate_invoices_count),
        asNumber(run.regenerate_count)
      );
    }

    return { flag, count };
  };

  const hasInvoicesToRegenerate = (run: InvoiceRun) => {
    const signals = getRegenerationSignals(run);
    return signals.flag || signals.count > 0;
  };

  const runsNeedingRegeneration = (dataFiltered || []).filter(
    hasInvoicesToRegenerate
  );

  return (
    <Page title="Invoices Run">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Invoices Run"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Invoices Run', href: '' },
          ]}
          action={
            allow.canCreate && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:play-circle-outline" />}
                onClick={handleOpenRunPopover}
                disabled={startInvoiceRunMutation.isLoading}
              >
                Start invoice run
              </Button>
            )
          }
        />
        <Popover
          id={runPopoverId}
          open={isRunPopoverOpen}
          anchorEl={runAnchorEl}
          onClose={handleCloseRunPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box component="form" onSubmit={handleSubmitInvoiceRun} sx={{ p: 3, width: 320 }}>
            <Stack spacing={2}>
              <TextField
                label="Cutoff date"
                type="date"
                value={runCutoffDate}
                onChange={(event) => setRunCutoffDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Days count"
                type="number"
                value={runDaysCount}
                onChange={(event) => setRunDaysCount(event.target.value)}
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={runIsDryRun}
                    onChange={(event) => setRunIsDryRun(event.target.checked)}
                  />
                }
                label="Dry run"
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={handleCloseRunPopover} color="inherit">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={startInvoiceRunMutation.isLoading}
                >
                  {startInvoiceRunMutation.isLoading ? 'Starting…' : 'Start run'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Popover>
        <Card>
          {invoicesRunLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <InvoiceRunsTableToolbar
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
                handleDatepickerChange={handleDatepickerChange}
                resetFilters={resetFilters}
              />

              {!!runsNeedingRegeneration.length && (
                <Alert
                  severity="warning"
                  sx={{ mx: 3, mb: 2 }}
                  icon={<Iconify icon="eva:alert-triangle-fill" />}
                >
                  <AlertTitle>Regeneration needed</AlertTitle>
                  {runsNeedingRegeneration.length === 1 ? (
                    <>Invoice run #{runsNeedingRegeneration[0].id} has invoices that need to be regenerated.</>
                  ) : (
                    <>
                      {runsNeedingRegeneration.length} invoice runs have invoices that need regeneration:{' '}
                      {runsNeedingRegeneration.map((run) => `#${run.id}`).join(', ')}.
                    </>
                  )}
                </Alert>
              )}

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={INVOICE_RUNS_TABLE_HEAD}
                      onSort={onSort}
                    />
                    <TableBody>
                      {dataFiltered
                        .slice(
                          page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((preview) => (
                      <InvoicesRunTableRow
                        key={preview.id}
                        row={preview}
                        showRegenerateWarning={hasInvoicesToRegenerate(preview)}
                      />
                    ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={emptyRows(
                          page,
                          rowsPerPage,
                          dataFiltered.length
                        )}
                      />

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No invoice runs found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={dataFiltered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
